import { useState, useMemo } from "react";
import { Link } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { Kpi } from "@/components/admin/kpi";
import { Segmented } from "@/components/admin/segmented";
import { AdminIcon, type IconName } from "@/components/admin/icon";
import { useToast } from "@/components/ui/feedback";
import {
	useCreateSegment,
	useSegmentPreview,
	useSegments,
} from "@/lib/customers/customers-hooks";
import { customersApi } from "@/lib/customers/customers-api";
import type { Segment } from "@/lib/customers/types";

interface FieldDef {
	value: string;
	label: string;
	group: string;
	icon: IconName;
	type: "enum" | "text" | "number" | "currency" | "date";
	options?: { value: string; label: string }[];
	suggestions?: string[];
	suffix?: string;
}

const FIELDS: FieldDef[] = [
	{
		value: "tier",
		label: "Palier fidélité",
		group: "Profil Client",
		icon: "star",
		type: "enum",
		options: [
			{ value: "bronze", label: "Bronze" },
			{ value: "silver", label: "Silver" },
			{ value: "gold", label: "Gold" },
			{ value: "platinum", label: "Platinum" },
			{ value: "vip", label: "VIP" },
		],
	},
	{
		value: "status",
		label: "Statut du compte",
		group: "Profil Client",
		icon: "user",
		type: "enum",
		options: [
			{ value: "active", label: "Actif" },
			{ value: "inactive", label: "Inactif" },
			{ value: "suspended", label: "Suspendu" },
			{ value: "prospect", label: "Prospect" },
		],
	},
	{
		value: "city",
		label: "Ville de résidence",
		group: "Localisation",
		icon: "pin",
		type: "text",
		suggestions: ["Douala", "Yaoundé", "Bafoussam", "Garoua", "Kribi", "Bamenda"],
	},
	{
		value: "orders_count",
		label: "Nombre de commandes",
		group: "Activité commerciale",
		icon: "cart",
		type: "number",
		suffix: "cmd(s)",
	},
	{
		value: "ltv",
		label: "Chiffre d'affaires total (LTV)",
		group: "Activité commerciale",
		icon: "zap",
		type: "currency",
		suffix: "FCFA",
	},
	{
		value: "consent",
		label: "Opt-in canal marketing",
		group: "Communication",
		icon: "bell",
		type: "enum",
		options: [
			{ value: "email", label: "Email" },
			{ value: "sms", label: "SMS" },
			{ value: "whatsapp", label: "WhatsApp" },
			{ value: "push", label: "Push App" },
		],
	},
	{
		value: "tag",
		label: "Étiquette / Tag manuel",
		group: "Profil Client",
		icon: "tag",
		type: "text",
		suggestions: ["VIP", "Pro", "Grossiste", "Ambassadeur", "Relance"],
	},
	{
		value: "gender",
		label: "Genre",
		group: "Profil Client",
		icon: "users",
		type: "enum",
		options: [
			{ value: "M", label: "Homme" },
			{ value: "F", label: "Femme" },
			{ value: "other", label: "Autre" },
		],
	},
	{
		value: "lang",
		label: "Langue préférée",
		group: "Communication",
		icon: "chat",
		type: "enum",
		options: [
			{ value: "fr", label: "Français (FR)" },
			{ value: "en", label: "Anglais (EN)" },
		],
	},
	{
		value: "type",
		label: "Type de compte",
		group: "Profil Client",
		icon: "user",
		type: "enum",
		options: [
			{ value: "individual", label: "Particulier" },
			{ value: "business", label: "Entreprise (B2B)" },
		],
	},
	{
		value: "acquisition_source",
		label: "Source d'acquisition",
		group: "Activité commerciale",
		icon: "trend",
		type: "enum",
		options: [
			{ value: "organic", label: "Recherche organique" },
			{ value: "referral", label: "Parrainage" },
			{ value: "ads", label: "Publicité" },
			{ value: "social", label: "Réseaux sociaux" },
			{ value: "direct", label: "Accès direct" },
		],
	},
	{
		value: "created_at",
		label: "Date de création",
		group: "Dates",
		icon: "calendar",
		type: "date",
	},
	{
		value: "last_seen_at",
		label: "Dernière activité",
		group: "Dates",
		icon: "clock",
		type: "date",
	},
];

const ALL_OPERATORS = [
	{ value: "eq", label: "= (Égal à)", forTypes: ["enum", "text", "number", "currency"] },
	{ value: "neq", label: "≠ (Différent de)", forTypes: ["enum", "text", "number", "currency"] },
	{ value: "gt", label: "> (Strictement supérieur)", forTypes: ["number", "currency", "date"] },
	{ value: "gte", label: "≥ (Supérieur ou égal)", forTypes: ["number", "currency", "date"] },
	{ value: "lt", label: "< (Strictement inférieur)", forTypes: ["number", "currency", "date"] },
	{ value: "lte", label: "≤ (Inférieur ou égal)", forTypes: ["number", "currency", "date"] },
	{ value: "ilike", label: "contient (texte)", forTypes: ["text"] },
	{ value: "in", label: "parmi (liste)", forTypes: ["enum", "text"] },
	{ value: "is_null", label: "est vide (non renseigné)", forTypes: ["enum", "text", "number", "currency", "date"] },
	{ value: "is_not_null", label: "est renseigné (présent)", forTypes: ["enum", "text", "number", "currency", "date"] },
];

export function AdminCustomerSegmentsPage() {
	const toast = useToast();
	const { data: segments, isLoading, isError, refetch } = useSegments();
	const preview = useSegmentPreview();
	const createSeg = useCreateSegment();

	const [name, setName] = useState("");
	const [kind, setKind] = useState<"dynamic" | "static">("dynamic");
	const [logic, setLogic] = useState<"AND" | "OR">("AND");
	const [conditions, setConditions] = useState([
		{ field: "tier", operator: "eq", value: "gold" },
	]);
	const [count, setCount] = useState<number | null>(null);

	// Filtres de recherche des segments enregistrés
	const [searchQuery, setSearchQuery] = useState("");
	const [filterKind, setFilterKind] = useState<"all" | "dynamic" | "static">("all");

	// Modales
	const [activeSegment, setActiveSegment] = useState<Segment | null>(null);
	const [showMembersModal, setShowMembersModal] = useState(false);
	const [membersData, setMembersData] = useState<{
		items: Array<{ id: string; name: string; email?: string; phone: string; tier?: string }>;
		total: number;
	} | null>(null);
	const [isMembersLoading, setIsMembersLoading] = useState(false);

	const [showImportModal, setShowImportModal] = useState(false);
	const [importIdsText, setImportIdsText] = useState("");
	const [isImporting, setIsImporting] = useState(false);

	const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
	const [editName, setEditName] = useState("");

	const rules = useMemo(() => ({ op: logic, conditions }), [logic, conditions]);

	// Métriques des segments
	const dynamicCount = useMemo(
		() => (segments ?? []).filter((s) => s.kind === "dynamic").length,
		[segments],
	);
	const staticCount = useMemo(
		() => (segments ?? []).filter((s) => s.kind === "static").length,
		[segments],
	);
	const totalMembersTargeted = useMemo(
		() => (segments ?? []).reduce((acc, s) => acc + (s.lastCount ?? 0), 0),
		[segments],
	);

	// Segments filtrés
	const filteredSegments = useMemo(() => {
		return (segments ?? []).filter((s) => {
			const matchKind = filterKind === "all" || s.kind === filterKind;
			const matchSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase().trim());
			return matchKind && matchSearch;
		});
	}, [segments, filterKind, searchQuery]);

	const addCond = () => {
		if (conditions.length >= 10) {
			toast.error("Limite atteinte : 10 conditions maximum.");
			return;
		}
		setConditions((c) => [...c, { field: "city", operator: "ilike", value: "" }]);
		setCount(null);
	};

	const setCond = (i: number, key: string, val: string) => {
		setConditions((cs) =>
			cs.map((c, idx) => {
				if (idx !== i) return c;
				const updated = { ...c, [key]: val };
				// Si changement de champ, réinitialiser l'opérateur et la valeur par défaut
				if (key === "field") {
					const fieldDef = FIELDS.find((f) => f.value === val);
					const validOps = ALL_OPERATORS.filter((o) =>
						fieldDef ? o.forTypes.includes(fieldDef.type) : true,
					);
					updated.operator = validOps[0]?.value ?? "eq";
					if (fieldDef?.type === "enum" && fieldDef.options?.[0]) {
						updated.value = fieldDef.options[0].value;
					} else {
						updated.value = "";
					}
				}
				return updated;
			}),
		);
		setCount(null);
	};

	const rmCond = (i: number) => {
		setConditions((cs) => cs.filter((_, idx) => idx !== i));
		setCount(null);
	};

	const runPreview = () => {
		if (conditions.length === 0) {
			toast.error("Veuillez ajouter au moins une condition.");
			return;
		}
		preview.mutate(rules, {
			onSuccess: (r) => {
				setCount(r.count);
				toast.info(`Aperçu calculé : ${r.count} client(s) éligible(s).`);
			},
			onError: () => toast.error("Erreur lors du calcul de l'aperçu. Vérifiez la valeur des conditions."),
		});
	};

	const save = () => {
		if (!name.trim()) {
			toast.error("Veuillez saisir un nom pour le segment.");
			return;
		}
		createSeg.mutate(
			{ name: name.trim(), kind, rulesJson: kind === "dynamic" ? rules : { op: "AND", conditions: [] } },
			{
				onSuccess: () => {
					toast.success(`Segment « ${name} » enregistré avec succès.`);
					setName("");
					setCount(null);
					refetch();
				},
				onError: () => toast.error("Erreur lors de l'enregistrement du segment."),
			},
		);
	};

	const handleDeleteSegment = async (seg: Segment) => {
		if (!confirm(`Confirmer la suppression du segment « ${seg.name} » ?`)) return;
		try {
			await customersApi.deleteSegment(seg.id);
			toast.success(`Segment « ${seg.name} » supprimé.`);
			refetch();
		} catch {
			toast.error("Erreur de suppression du segment.");
		}
	};

	const handleViewMembers = async (seg: Segment) => {
		setActiveSegment(seg);
		setShowMembersModal(true);
		setIsMembersLoading(true);
		try {
			const res = await customersApi.segmentMembers(seg.id);
			setMembersData(res);
		} catch {
			toast.error("Impossible de charger les membres du segment.");
		} finally {
			setIsMembersLoading(false);
		}
	};

	const handleImportStatic = async () => {
		if (!activeSegment || !importIdsText.trim()) return;
		const ids = importIdsText
			.split(/[\n,;]+/)
			.map((s) => s.trim())
			.filter(Boolean);
		if (ids.length === 0) {
			toast.error("Aucun identifiant client valide trouvé.");
			return;
		}
		setIsImporting(true);
		try {
			await customersApi.importStaticSegment(activeSegment.id, ids);
			toast.success(`${ids.length} membre(s) importé(s) dans le segment.`);
			setShowImportModal(false);
			setImportIdsText("");
			refetch();
		} catch {
			toast.error("Erreur lors de l'importation statique.");
		} finally {
			setIsImporting(false);
		}
	};

	const handleSaveEditSegment = async () => {
		if (!editingSegment || !editName.trim()) return;
		try {
			await customersApi.updateSegment(editingSegment.id, { name: editName.trim() });
			toast.success("Segment mis à jour.");
			setEditingSegment(null);
			refetch();
		} catch {
			toast.error("Erreur lors de la mise à jour du segment.");
		}
	};

	const renderRuleSummary = (seg: Segment) => {
		if (seg.kind === "static") return "Segment statique (import d'identifiants)";
		const r = seg.rulesJson as { op?: string; conditions?: Array<{ field: string; operator: string; value: unknown }> };
		if (!r || !Array.isArray(r.conditions) || r.conditions.length === 0) {
			return "Aucune règle définie";
		}
		const parts = r.conditions.map((c) => {
			const fDef = FIELDS.find((f) => f.value === c.field);
			const opDef = ALL_OPERATORS.find((o) => o.value === c.operator);
			const fLabel = fDef?.label ?? c.field;
			const opLabel = opDef?.label.split(" ")[0] ?? c.operator;
			if (c.operator === "is_null") return `${fLabel} est vide`;
			if (c.operator === "is_not_null") return `${fLabel} est présent`;
			return `${fLabel} ${opLabel} ${String(c.value)}`;
		});
		const joiner = r.op === "OR" ? " OU " : " ET ";
		return parts.join(joiner);
	};

	return (
		<>
			<PageHead
				title="Segmentation Clients"
				sub="Builder dynamique de segments comportementaux et listes ciblées (CUS-04)"
			/>

			{/* Barre de métriques KPI */}
			<div className="kpi-grid" style={{ marginBottom: 20 }}>
				<Kpi
					label="Total Segments"
					value={(segments ?? []).length}
					icon="tag"
					variant="default"
				/>
				<Kpi
					label="Segments Dynamiques"
					value={dynamicCount}
					icon="zap"
					variant="accent"
				/>
				<Kpi
					label="Segments Statiques"
					value={staticCount}
					icon="users"
					variant="info"
				/>
				<Kpi
					label="Audience Cumulée"
					value={totalMembersTargeted.toLocaleString("fr-FR")}
					icon="user"
					variant="forest"
				/>
			</div>

			<div className="grid-2" style={{ alignItems: "start" }}>
				{/* Carte Builder de Règles */}
				<Card style={{ alignSelf: "start" }}>
					<CardHead
						title="Builder de Règles"
						sub={`Moteur de filtrage dynamique · ${conditions.length}/10 condition(s)`}
					/>
					<CardBody>
						{/* Type de segment et nom */}
						<div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
							<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
								<label style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-soft)" }}>
									Type de segment :
								</label>
								<Segmented
									value={kind}
									onChange={(v) => setKind(v)}
									options={[
										{ value: "dynamic", label: "⚡ Dynamique (Règles)" },
										{ value: "static", label: "📁 Statique (Import)" },
									]}
								/>
							</div>

							<div style={{ display: "flex", gap: 8 }}>
								<input
									className="inp"
									style={{ flex: 1 }}
									placeholder="Nom du segment (ex: Clients Gold Yaoundé)"
									value={name}
									onChange={(e) => setName(e.target.value)}
									aria-label="Nom du segment"
								/>
							</div>
						</div>

						{/* Logique d'agrégation si dynamique */}
						{kind === "dynamic" && (
							<>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										background: "var(--a-bg-soft, #f6f5f2)",
										padding: "8px 12px",
										borderRadius: 8,
										marginBottom: 14,
										border: "1px solid var(--a-border)",
									}}
								>
									<span style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-secondary)" }}>
										Combiner les conditions avec :
									</span>
									<Segmented
										value={logic}
										onChange={(v) => {
											setLogic(v);
											setCount(null);
										}}
										options={[
											{ value: "AND", label: "TOUTES (AND)" },
											{ value: "OR", label: "AU MOINS UNE (OR)" },
										]}
									/>
								</div>

								{/* Liste des conditions */}
								<div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
									{conditions.map((c, i) => {
										const currentFieldDef = FIELDS.find((f) => f.value === c.field);
										const validOperators = ALL_OPERATORS.filter((o) =>
											currentFieldDef ? o.forTypes.includes(currentFieldDef.type) : true,
										);
										const isNullOp = c.operator === "is_null" || c.operator === "is_not_null";

										return (
											<div
												key={i}
												style={{
													display: "grid",
													gridTemplateColumns: "minmax(140px, 1.2fr) minmax(130px, 1fr) minmax(140px, 1.5fr) auto",
													gap: 8,
													alignItems: "center",
													background: "var(--a-surface, #fff)",
													padding: "10px 12px",
													borderRadius: 8,
													border: "1px solid var(--a-border)",
												}}
											>
												{/* Champ */}
												<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
													{currentFieldDef?.icon && (
														<AdminIcon n={currentFieldDef.icon} s={14} c="var(--a-ink-soft)" />
													)}
													<select
														className="inp"
														style={{ flex: 1, fontSize: 13 }}
														value={c.field}
														onChange={(e) => setCond(i, "field", e.target.value)}
														aria-label={`Champ condition ${i + 1}`}
													>
														{FIELDS.map((f) => (
															<option key={f.value} value={f.value}>
																{f.label}
															</option>
														))}
													</select>
												</div>

												{/* Opérateur */}
												<select
													className="inp"
													style={{ fontSize: 13 }}
													value={c.operator}
													onChange={(e) => setCond(i, "operator", e.target.value)}
													aria-label={`Opérateur condition ${i + 1}`}
												>
													{validOperators.map((o) => (
														<option key={o.value} value={o.value}>
															{o.label}
														</option>
													))}
												</select>

												{/* Valeur intelligente */}
												{isNullOp ? (
													<div
														style={{
															fontSize: 12,
															fontStyle: "italic",
															color: "var(--a-ink-mute)",
															padding: "6px 8px",
															background: "var(--a-bg-soft, #f9f8f6)",
															borderRadius: 6,
														}}
													>
														Aucune valeur requise
													</div>
												) : currentFieldDef?.type === "enum" && currentFieldDef.options ? (
													<select
														className="inp"
														style={{ fontSize: 13 }}
														value={String(c.value)}
														onChange={(e) => setCond(i, "value", e.target.value)}
														aria-label={`Valeur condition ${i + 1}`}
													>
														{currentFieldDef.options.map((opt) => (
															<option key={opt.value} value={opt.value}>
																{opt.label}
															</option>
														))}
													</select>
												) : currentFieldDef?.type === "number" || currentFieldDef?.type === "currency" ? (
													<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
														<input
															type="number"
															className="inp"
															style={{ flex: 1, fontSize: 13 }}
															placeholder="0"
															value={String(c.value)}
															onChange={(e) => setCond(i, "value", e.target.value)}
															aria-label={`Valeur numérique ${i + 1}`}
														/>
														{currentFieldDef.suffix && (
															<span style={{ fontSize: 11, color: "var(--a-ink-mute)", fontWeight: 500 }}>
																{currentFieldDef.suffix}
															</span>
														)}
													</div>
												) : currentFieldDef?.type === "date" ? (
													<input
														type="date"
														className="inp"
														style={{ fontSize: 13 }}
														value={String(c.value)}
														onChange={(e) => setCond(i, "value", e.target.value)}
														aria-label={`Date condition ${i + 1}`}
													/>
												) : (
													<input
														className="inp"
														style={{ fontSize: 13 }}
														placeholder="Entrez une valeur..."
														value={String(c.value)}
														onChange={(e) => setCond(i, "value", e.target.value)}
														aria-label={`Valeur texte ${i + 1}`}
													/>
												)}

												{/* Bouton de suppression */}
												<AdminButton
													variant="ghost"
													size="sm"
													disabled={conditions.length <= 1}
													onClick={() => rmCond(i)}
													title="Supprimer la condition"
												>
													<AdminIcon n="close" s={14} c="var(--a-danger, #b23a2a)" />
												</AdminButton>
											</div>
										);
									})}
								</div>
							</>
						)}

						{/* Actions du builder */}
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: 8,
								marginTop: 14,
								flexWrap: "wrap",
							}}
						>
							{kind === "dynamic" ? (
								<AdminButton variant="ghost" onClick={addCond} disabled={conditions.length >= 10}>
									<AdminIcon n="plus" s={14} /> Ajouter une condition ({conditions.length}/10)
								</AdminButton>
							) : (
								<span className="muted" style={{ fontSize: 12 }}>
									Segment statique : les membres seront ajoutés via import d'identifiants.
								</span>
							)}

							<div style={{ display: "flex", gap: 8 }}>
								{kind === "dynamic" && (
									<AdminButton variant="secondary" disabled={preview.isPending} onClick={runPreview}>
										<AdminIcon n="eye" s={14} /> Aperçu
									</AdminButton>
								)}
								<AdminButton
									variant="primary"
									disabled={!name.trim() || createSeg.isPending}
									onClick={save}
								>
									Enregistrer le segment
								</AdminButton>
							</div>
						</div>

						{/* Résultat d'aperçu */}
						{count !== null && (
							<div
								style={{
									marginTop: 16,
									padding: "14px 16px",
									background: "var(--a-forest-soft, #e2ebe5)",
									border: "1px solid var(--a-forest, #1e4d3b)",
									borderRadius: 10,
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div>
									<div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--a-forest, #1e4d3b)", fontWeight: 700 }}>
										Audience estimée
									</div>
									<div style={{ fontSize: 22, fontWeight: 900, color: "var(--a-ink-main)" }}>
										{count.toLocaleString("fr-FR")} <span style={{ fontSize: 14, fontWeight: 500 }}>client(s) éligible(s)</span>
									</div>
								</div>
								{preview.data && (
									<Tag variant="success" noDot>
										Calculé en {preview.data.ms} ms
									</Tag>
								)}
							</div>
						)}
					</CardBody>
				</Card>

				{/* Carte Segments enregistrés */}
				<Card>
					<CardHead
						title="Segments enregistrés"
						sub={`${filteredSegments.length} segment(s) configuré(s)`}
					/>
					<CardBody>
						{/* Barre de recherche et filtre par type */}
						<div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
							<div style={{ position: "relative", flex: 1, minWidth: 160 }}>
								<input
									className="inp"
									style={{ width: "100%", paddingLeft: 30, fontSize: 12 }}
									placeholder="Rechercher un segment..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
								/>
								<div style={{ position: "absolute", left: 10, top: 10 }}>
									<AdminIcon n="search" s={14} c="var(--a-ink-mute)" />
								</div>
							</div>
							<Segmented
								value={filterKind}
								onChange={(v) => setFilterKind(v)}
								options={[
									{ value: "all", label: "Tous" },
									{ value: "dynamic", label: "Dynamiques" },
									{ value: "static", label: "Statiques" },
								]}
							/>
						</div>

						{isLoading && <div className="muted" style={{ padding: 12 }}>Chargement des segments…</div>}
						{isError && <div className="muted" style={{ padding: 12, color: "var(--a-danger)" }}>Erreur lors du chargement des segments.</div>}
						{!isLoading && !isError && filteredSegments.length === 0 && (
							<div className="muted" style={{ padding: "20px 0", textAlign: "center" }}>
								Aucun segment ne correspond à votre recherche.
							</div>
						)}

						<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
							{filteredSegments.map((s) => (
								<div
									key={s.id}
									style={{
										padding: 14,
										borderRadius: 10,
										border: "1px solid var(--a-border)",
										background: "var(--a-surface, #fff)",
										display: "flex",
										flexDirection: "column",
										gap: 8,
										transition: "border-color 0.15s ease",
									}}
								>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
										<div>
											<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
												<strong style={{ fontSize: 14, color: "var(--a-ink-main)" }}>{s.name}</strong>
												<Tag variant={s.kind === "dynamic" ? "terra" : "info"} noDot>
													{s.kind === "dynamic" ? "⚡ Dynamique" : "📁 Statique"}
												</Tag>
											</div>
											<div style={{ fontSize: 12, color: "var(--a-ink-soft)", marginTop: 4 }}>
												{renderRuleSummary(s)}
											</div>
										</div>

										<div style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", color: "var(--a-ink-main)", background: "var(--a-bg-soft)", padding: "4px 8px", borderRadius: 6 }}>
											{s.lastCount != null ? `${s.lastCount.toLocaleString("fr-FR")} membre(s)` : "—"}
										</div>
									</div>

									{/* Actions */}
									<div style={{ display: "flex", justifyContent: "flex-end", gap: 6, paddingTop: 6, borderTop: "1px dashed var(--a-border)" }}>
										<AdminButton variant="ghost" size="sm" onClick={() => handleViewMembers(s)}>
											<AdminIcon n="users" s={13} /> Membres
										</AdminButton>
										{s.kind === "static" && (
											<AdminButton
												variant="ghost"
												size="sm"
												onClick={() => {
													setActiveSegment(s);
													setShowImportModal(true);
												}}
											>
												<AdminIcon n="plus" s={13} /> Importer
											</AdminButton>
										)}
										<AdminButton
											variant="ghost"
											size="sm"
											onClick={() => {
												setEditingSegment(s);
												setEditName(s.name);
											}}
										>
											<AdminIcon n="edit" s={13} /> Éditer
										</AdminButton>
										<AdminButton
											variant="ghost"
											size="sm"
											style={{ color: "var(--a-danger, #B23A2A)" }}
											onClick={() => handleDeleteSegment(s)}
										>
											<AdminIcon n="trash" s={13} />
										</AdminButton>
									</div>
								</div>
							))}
						</div>
					</CardBody>
				</Card>
			</div>

			{/* Modale des membres du segment */}
			{showMembersModal && activeSegment && (
				<div role="dialog" aria-modal="true" style={modalOverlayStyle}>
					<div style={{ ...modalBoxStyle, width: "min(640px, 100%)" }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<h3 style={{ margin: 0, fontSize: 16 }}>Membres du segment « {activeSegment.name} »</h3>
							<AdminButton variant="ghost" size="sm" onClick={() => setShowMembersModal(false)}>
								<AdminIcon n="close" s={16} />
							</AdminButton>
						</div>

						<div style={{ maxHeight: 360, overflowY: "auto" }}>
							{isMembersLoading && <div className="muted" style={{ padding: 12 }}>Chargement de la liste des membres…</div>}
							{!isMembersLoading && (membersData?.items ?? []).length === 0 && (
								<div className="muted" style={{ padding: "20px 0", textAlign: "center" }}>
									Aucun membre trouvé dans ce segment.
								</div>
							)}
							{!isMembersLoading && (membersData?.items ?? []).length > 0 && (
								<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
									<thead>
										<tr style={{ background: "var(--a-bg-soft)", textAlign: "left", color: "var(--a-ink-soft)", fontSize: 11, textTransform: "uppercase" }}>
											<th style={{ padding: "8px 10px" }}>Client</th>
											<th style={{ padding: "8px 10px" }}>Téléphone / Email</th>
											<th style={{ padding: "8px 10px", textAlign: "right" }}>Action</th>
										</tr>
									</thead>
									<tbody>
										{membersData?.items.map((m) => (
											<tr key={m.id} style={{ borderBottom: "1px solid var(--a-border)" }}>
												<td style={{ padding: "8px 10px", fontWeight: 600 }}>
													<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
														<div
															style={{
																width: 28,
																height: 28,
																borderRadius: "50%",
																background: "var(--a-primary-soft, #fbe8e0)",
																color: "var(--a-primary, #c6563a)",
																display: "grid",
																placeItems: "center",
																fontSize: 12,
																fontWeight: 700,
															}}
														>
															{m.name.charAt(0).toUpperCase()}
														</div>
														{m.name}
													</div>
												</td>
												<td style={{ padding: "8px 10px", color: "var(--a-ink-soft)", fontFamily: "var(--a-font-mono, monospace)", fontSize: 12 }}>
													{m.phone} {m.email ? `· ${m.email}` : ""}
												</td>
												<td style={{ padding: "8px 10px", textAlign: "right" }}>
													<Link to={`/clients/${m.id}`} target="_blank">
														<AdminButton variant="ghost" size="sm">
															Voir profil <AdminIcon n="arrowUR" s={12} />
														</AdminButton>
													</Link>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>

						<div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
							<AdminButton variant="secondary" onClick={() => setShowMembersModal(false)}>
								Fermer
							</AdminButton>
						</div>
					</div>
				</div>
			)}

			{/* Modale d'import statique */}
			{showImportModal && activeSegment && (
				<div role="dialog" aria-modal="true" style={modalOverlayStyle}>
					<div style={modalBoxStyle}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<h3 style={{ margin: 0, fontSize: 16 }}>Importer des membres (Segment Statique)</h3>
							<AdminButton variant="ghost" size="sm" onClick={() => setShowImportModal(false)}>
								<AdminIcon n="close" s={16} />
							</AdminButton>
						</div>

						<p className="muted" style={{ fontSize: 13, margin: 0 }}>
							Collez la liste des identifiants clients (UUIDs) séparés par des virgules ou des retours à la ligne.
						</p>

						<textarea
							className="inp"
							rows={6}
							style={{ fontFamily: "monospace", fontSize: 12 }}
							placeholder="4f89d31a-7b2c-4e89-9a12-8c76b5d4e3f2&#10;9b12a34c-5d6e-7f89-0a1b-2c3d4e5f6a7b..."
							value={importIdsText}
							onChange={(e) => setImportIdsText(e.target.value)}
						/>

						<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
							<AdminButton variant="ghost" onClick={() => setShowImportModal(false)}>
								Annuler
							</AdminButton>
							<AdminButton variant="primary" disabled={isImporting || !importIdsText.trim()} onClick={handleImportStatic}>
								{isImporting ? "Importation en cours…" : "Lancer l'import"}
							</AdminButton>
						</div>
					</div>
				</div>
			)}

			{/* Modale d'édition de segment */}
			{editingSegment && (
				<div role="dialog" aria-modal="true" style={modalOverlayStyle}>
					<div style={modalBoxStyle}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<h3 style={{ margin: 0, fontSize: 16 }}>Modifier le segment</h3>
							<AdminButton variant="ghost" size="sm" onClick={() => setEditingSegment(null)}>
								<AdminIcon n="close" s={16} />
							</AdminButton>
						</div>

						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<label style={{ fontSize: 12, fontWeight: 600, color: "var(--a-ink-soft)" }}>
								Nom du segment
							</label>
							<input
								className="inp"
								value={editName}
								onChange={(e) => setEditName(e.target.value)}
							/>
						</div>

						<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
							<AdminButton variant="ghost" onClick={() => setEditingSegment(null)}>
								Annuler
							</AdminButton>
							<AdminButton variant="primary" disabled={!editName.trim()} onClick={handleSaveEditSegment}>
								Enregistrer
							</AdminButton>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

const modalOverlayStyle = {
	position: "fixed" as const,
	inset: 0,
	zIndex: 1000,
	background: "rgba(20,16,12,0.45)",
	display: "grid",
	placeItems: "center",
	padding: 16,
};

const modalBoxStyle = {
	width: "min(520px, 100%)",
	background: "var(--a-surface, #fff)",
	border: "1px solid var(--a-border)",
	borderRadius: 12,
	padding: 20,
	display: "grid",
	gap: 14,
};
